'use client'

import { FC, useEffect, useRef, useState } from 'react'
import { BodySize, HeaderHeight } from '../constants'
import { RenderedSectionsType } from '@/components/ComposedPage/types'

interface DynamicPageSectionsProps {
  renderedSections?: RenderedSectionsType[]
}

const getHeaderHeight = (windowWidth: number) => {
  if (windowWidth > BodySize.maxMobile) {
    return HeaderHeight.large
  } else if (
    windowWidth <= BodySize.maxMobile &&
    windowWidth >= BodySize.maxSmallMobile
  ) {
    return HeaderHeight.medium
  } else {
    return HeaderHeight.small
  }
}

export const DynamicPageSections: FC<DynamicPageSectionsProps> = ({
  renderedSections,
}) => {
  const firstSectionRef = useRef<HTMLDivElement | null>(null)
  const [{ width, offset }, offsetState] = useState({
    offset: 0,
    width: 0,
  })

  const calculateOffset = () => {
    const newWidth = window.innerWidth
    const elementHeight = firstSectionRef.current?.clientHeight || 0
    const headerHeight = getHeaderHeight(window.innerWidth)

    if (firstSectionRef.current && width !== newWidth) {
      if (elementHeight >= window.innerHeight - headerHeight) {
        const diff =
          window.innerHeight - (firstSectionRef.current?.clientHeight || 0)

        offsetState({
          width: window.innerWidth,
          offset: Math.min(headerHeight, diff),
        })
      } else {
        offsetState({
          width: window.innerWidth,
          offset: headerHeight,
        })
      }
    }
  }

  // Intentionally empty dep array for initial render update. Width changes handled in resize callback.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => calculateOffset(), [])

  useEffect(() => {
    window.addEventListener('resize', calculateOffset)

    return () => window.removeEventListener('resize', calculateOffset)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [width])

  return (
    <>
      {renderedSections?.map(({ component, id, parallax }, index) => (
        <div
          data-testid={`index${index}`}
          key={id}
          style={!index && parallax ? { position: 'sticky', top: offset } : {}}
          ref={!index && parallax ? firstSectionRef : null}
        >
          {component}
        </div>
      ))}
    </>
  )
}
